import React, { useState, useMemo, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useLocation } from 'react-router-dom';
import {
    Filter, Search, X, Settings, Edit2, MoreVertical,
    ChevronDown, Trash2, CheckCircle, Clock,
    ArrowRightLeft, UserCheck, Mail, Calendar,
    TrendingUp, Info, Plus, Pencil, MessageSquare,
    Mic, PlusSquare, FileText, CheckSquare, Download, ChevronLeft
} from 'lucide-react';
import { Box, Drawer } from '@mui/material';
import API_ENDPOINTS from '../../../config/apiEndpoints';
import Loader from '../../../components/Loader/Loader';
import PipelineDealActionsOperation from './PipelineDealActionsOperation';
import SubHeader from '../../../components/common/SubHeader/SubHeader';
import LeadActionOperation from '../../../common/LeadActionOperation/LeadActionOperation';
import usePermissions from '../../Master_Settings/usePermissions';
import './PipelineDealModule.css';

// Stages per pipeline will be fetched from API
const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
const Pipeline_stage = `${NEW_BASE_URL}${API_ENDPOINTS.COMMON.PIPELINE_STAGE_BASED_ON_DEALPIPELINE}`;
const Custome_Search = `${NEW_BASE_URL}${API_ENDPOINTS.PIPELINE.GET_CUSTOME_SEARCH}`;
const BACKEND_BASEURL_FOR_DEVELOPMENT = "http://dev3services.kit19.com/";
const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
const HIRERARCHY_LIST = `${NEW_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.GET_USER_HIRERARCHY_LIST}`;
const NEW_ADDED_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
const USER_CRM_STAGEWISE = `${NEW_BASE_URL}${API_ENDPOINTS.PIPELINE.PIPELINE_STAGEWISE_DATA}`;
const Get_Pipeline_List = `${NEW_BASE_URL}${API_ENDPOINTS.PIPELINE.GET_PIPELINE_LIST}`;

// Helper to format YYYY-MM-DD to DD-MMM-YYYY
const formatDateToDDMMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate().toString().padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
};

// Default Date range logic (Today and 7 days ago)
const getISODate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const todayObj = new Date();
const DEFAULT_DATE_TO = getISODate(todayObj);
const weekAgoObj = new Date();
weekAgoObj.setDate(weekAgoObj.getDate() - 7);
const DEFAULT_DATE_FROM = getISODate(weekAgoObj);

const PipelineDealModule = () => {
    const location = useLocation();
    const { permissions } = usePermissions('PAG10038');
    const dealPerms = useMemo(() => {
        if (!permissions || !Array.isArray(permissions)) return null;
        return permissions.find(p => p.ModuleCode?.includes('PAG10038')) || {};
    }, [permissions]);

    // --- State ---
    const [pipelines, setPipelines] = useState([]);
    const [selectedPipeline, setSelectedPipeline] = useState(null); // default: loaded from API
    
let deviceTokenPromise = null;

    // --- Dynamic Token Fetching ---
    const fetchDeviceToken = async () => {
        const cachedToken = sessionStorage.getItem('deviceToken');
        if (cachedToken && cachedToken !== "-1" && cachedToken !== "null" && cachedToken !== "undefined") {
            return cachedToken;
        }

        if (deviceTokenPromise) return deviceTokenPromise;

        deviceTokenPromise = (async () => {
            try {
            const GET_TOKEN_FOR_SAVE_EDIT = `${NEW_ADDED_BASE_URL}${API_ENDPOINTS.COMMON.GET_TOKEN}`;
            const payload = {
                Status: "",
                Message: "",
                Details: {
                    LoginName: localStorage.getItem("LoginName") || localStorage.getItem("username") || localStorage.getItem("UserLogin") || "Abhi01",
                    Password: localStorage.getItem("Password") || localStorage.getItem("password") || localStorage.getItem("UserPassword") || "770940",
                    Url: localStorage.getItem("Url") || localStorage.getItem("domain") || "www.kit19.com",
                    DeviceId: localStorage.getItem("DeviceId") || localStorage.getItem("deviceId") || localStorage.getItem("DEVICE_ID") || "10-60-4B-7A-92-E5"
                }
            };

            const response = await fetch(GET_TOKEN_FOR_SAVE_EDIT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const rawText = await response.text();
                const statusMatch = rawText.match(/"Status"\s*:\s*(\d+)/);
                if (statusMatch && statusMatch[1] === '1') {
                    const tokenMatch = rawText.match(/"Token"\s*:\s*(-?\d+)/);
                    if (tokenMatch && tokenMatch[1] && tokenMatch[1] !== "-1") {
                        const tokenStr = tokenMatch[1];
                        sessionStorage.setItem('deviceToken', tokenStr);
                        return tokenStr;
                    }
                }
            }
            } catch (error) {
                console.error("Error fetching device token:", error);
            }
            
            // Clear invalid token cache
            sessionStorage.removeItem('deviceToken');
            
            // Fallback to Kit19 standard auth token if device token fails
            const kitToken = localStorage.getItem('Token') || localStorage.getItem('kit_token') || localStorage.getItem('kit_sso_token');
            if (kitToken) return kitToken;

            return API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN.toString();
        })();

        try {
            const token = await deviceTokenPromise;
            deviceTokenPromise = null;
            return token;
        } catch (e) {
            deviceTokenPromise = null;
            return API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN.toString();
        }
    };

    // --- Dynamic Pipeline Fetching ---
    const fetchPipelines = async () => {
        setLoading(true);
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: TOKEN,
                Details: parseInt(uid, 10)
            };

            const response = await fetch(Get_Pipeline_List, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                const data = result?.Details || result?.d || [];
                if (Array.isArray(data) && data.length > 0) {
                    const mapped = data.map(p => ({
                        ...p,
                        PipeLineID: (p.PipeLineID || p.PipeLineId || p.pipelineId || p.Id || p.id || p.Code || p.code || "").toString(),
                        PipelineName: p.PipelineName || p.Pipeline || p.name || p.Name || p.Text || p.text || "",
                        DealExpiry: (p.DealExpiry || p.dealExpiry || "30").toString(),
                        IsDefault: (p.IsDefault || p.isDefault || "False").toString()
                    }));
                    setPipelines(mapped);

                    // Determine default selected pipeline
                    let defaultPipeline = null;
                    if (location.state?.selectedPipelineName) {
                        defaultPipeline = mapped.find(p =>
                            p.PipelineName.toLowerCase() === location.state.selectedPipelineName.toLowerCase()
                        );
                    }
                    if (!defaultPipeline) {
                        defaultPipeline = mapped.find(p => p.IsDefault?.toLowerCase() === "true");
                    }
                    if (!defaultPipeline) {
                        defaultPipeline = mapped[0];
                    }

                    setSelectedPipeline(defaultPipeline);
                } else {
                    setPipelines([]);
                    setSelectedPipeline(null);
                }
            }
        } catch (error) {
            console.error("Error fetching pipeline list:", error);
            setPipelines([]);
            setSelectedPipeline(null);
        } finally {
            setLoading(false);
        }
    };
    const [stages, setStages] = useState([]);
    const [dealsByStage, setDealsByStage] = useState({});
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownSearch, setDropdownSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState('kanban'); // 'kanban' or 'table'
    const [isLeadActionOpen, setIsLeadActionOpen] = useState(false);
    const [selectedDealForEdit, setSelectedDealForEdit] = useState(null);
    const [editMode, setEditMode] = useState('edit'); // 'edit', 'sms', 'email', 'voice', etc.
    const [activeMenu, setActiveMenu] = useState(null); // dealId if menu is open
    const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0, bottom: 'auto' });
    const [selectedDealForMenu, setSelectedDealForMenu] = useState(null);
    const [selectedStageForMenu, setSelectedStageForMenu] = useState(null);
    const [customSearchOptions, setCustomSearchOptions] = useState([]);
    const [selectedCustomSearch, setSelectedCustomSearch] = useState(null);
    const [customSearchDropdownOpen, setCustomSearchDropdownOpen] = useState(false);
    const [customSearchDropdownSearch, setCustomSearchDropdownSearch] = useState('');
    const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);

    // --- Advanced Filter State ---
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [salesOwners, setSalesOwners] = useState([]);
    const [selectedSalesOwners, setSelectedSalesOwners] = useState([]);
    const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
    const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);

    const [filterPipeline, setFilterPipeline] = useState(null);
    const [pipelineDropdownOpen, setPipelineDropdownOpen] = useState(false);
    const [filterStages, setFilterStages] = useState([]);
    const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
    const [selectedFilterStages, setSelectedFilterStages] = useState([]); // Multiple select
    const [hoveredDealId, setHoveredDealId] = useState(null); // For amount tooltip

    // Refs for filter date inputs
    const createdFromRef = useRef(null);
    const createdToRef = useRef(null);
    const closureFromRef = useRef(null);
    const closureToRef = useRef(null);


    const [filterValues, setFilterValues] = useState({
        createdDateFrom: DEFAULT_DATE_FROM,
        createdDateTo: DEFAULT_DATE_TO,
        estimatedAmountFrom: '0',
        estimatedAmountTo: '9999999',
        expiryDealsOnly: false,
        isDeletedOnly: false,
        closureDateFrom: '',
        closureDateTo: ''
    });

    // --- Fetch Sales Owners ---
    const fetchSalesOwners = async () => {
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const payload = { Token: TOKEN, Details: { UserId: parseInt(uid) } };

            const response = await fetch(HIRERARCHY_LIST, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const result = await response.json();
            const rawData = result?.Details?.data || result?.Details || result?.d || [];
            let parsed = Array.isArray(rawData) ? rawData : [];
            if (typeof rawData === 'string') { try { parsed = JSON.parse(rawData); } catch(e){} }
            const data = Array.isArray(parsed) ? parsed : [];

            // Map the data to a standard format since the API returns different fields
            const formattedData = Array.isArray(data) ? data.map(o => ({
                User_ID: o.Code || o.User_ID || o.Id,
                DisplayName: o.Text || (o.FName ? `${o.FName} ${o.LName}` : 'Unknown'),
                OriginalData: o
            })) : [];

            setSalesOwners(formattedData);
        } catch (error) {
            console.error("Error fetching sales owners:", error);
            setSalesOwners([]);
        }
    };

    useEffect(() => {
        const handleGlobalScroll = () => {
            if (activeMenu) setActiveMenu(null);
        };
        window.addEventListener('scroll', handleGlobalScroll, true);
        return () => window.removeEventListener('scroll', handleGlobalScroll, true);
    }, [activeMenu]);

    // --- Fetch Stages for Filter ---
    const fetchFilterStages = async (pipelineId) => {
        if (!pipelineId) {
            setFilterStages([]);
            return;
        }
        try {
            const payload = {
                Status: "",
                Message: "",
                Token: TOKEN,
                Details: parseInt(pipelineId) || pipelineId
            };
            const response = await fetch(Pipeline_stage, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                const result = await response.json();
                const data = result?.Details || result?.d || [];
                if (Array.isArray(data)) {
                    setFilterStages(data.map(s => ({
                        Id: (s.Id || s.StageID)?.toString(),
                        Name: s.Name || s.StageName
                    })));
                }
            }
        } catch (error) {
            console.error("Error fetching filter stages:", error);
        }
    };

    // --- Fetch Custom Search Options ---
    const fetchCustomSearch = async () => {
        try {
            const token = await fetchDeviceToken();
            const uid = localStorage.getItem('userId') || '34594';
            const payload = {
                Token: token || TOKEN,
                Details: {
                    userId: parseInt(uid)
                }
            };
            console.log("Fetching custom search with payload:", payload);

            const response = await fetch(Custome_Search, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();
            console.log("Custom Search API Response:", result);

            const data = result?.Details || result?.d || [];
            setCustomSearchOptions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching custom search:", error);
            setCustomSearchOptions([]);
        }
    };

    // --- Fetch Stages from API ---
    const fetchStages = async (pipelineId) => {
        if (!pipelineId) return;
        setLoading(true);
        try {
            const payload = {
                Status: "",
                Message: "",
                Token: TOKEN,
                Details: parseInt(pipelineId) || pipelineId
            };
            console.log("Fetching stages with payload:", payload);

            const response = await fetch(Pipeline_stage, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log("Pipeline Stages API Response:", result);

            const data = result?.Details || result?.d || result?.data || result;

            let rawStages = Array.isArray(data) ? data : [];
            console.log("Extracted rawStages:", rawStages);

            if (rawStages.length > 0) {
                const mappedStages = rawStages.map(s => {
                    const id = (s.Id || s.StageID || s.StageId)?.toString();
                    return {
                        ...s,
                        Id: id,
                        StageID: id,
                        StageId: id,
                        StageName: s.Name || s.StageName || s.Name
                    };
                });
                console.log("Mapped stages to state:", mappedStages);
                setStages(mappedStages);

                // Fetch deals for these stages
                const stageIds = mappedStages.map(s => s.StageID);
                fetchDealData(pipelineId, stageIds);
            } else {
                console.warn("No stages found in the extracted data");
                setStages([]);
                setDealsByStage({});
            }
        } catch (error) {
            console.error("Error fetching stages:", error);
            setStages([]);
            setDealsByStage({});
        } finally {
            setLoading(false);
        }
    };

    // --- Fetch Deal Data (Stagewise) ---
    const fetchDealData = async (pipelineId, stageList = []) => {
        setLoading(true);
        try {
            const uid = localStorage.getItem('userId') || '34594';
            const parentId = localStorage.getItem('parentId') || '0';

            // Build stage list if not provided
            const finalStageList = stageList.length > 0
                ? stageList
                : stages.map(s => s.StageID);

            const payload = {
                Token: TOKEN,
                LoggedUserId: uid,
                ParentId: parentId,
                Details: {
                    UserId: parseInt(uid),
                    strPipelineID: pipelineId?.toString() || selectedPipeline?.PipeLineID?.toString(),
                    strSalesOwnerList: selectedSalesOwners.map(o => o.User_ID),
                    blExpiryStatus: filterValues.expiryDealsOnly,
                    blIsDeleteStatus: filterValues.isDeletedOnly,
                    strAmountFrom: filterValues.estimatedAmountFrom,
                    strAmountTo: filterValues.estimatedAmountTo,
                    strCriteriaName: (!selectedCustomSearch || selectedCustomSearch?.CriteriaName === "All Leads" || selectedCustomSearch?.Text === "All Leads" || selectedCustomSearch?.Name === "All Leads")
                        ? ""
                        : (selectedCustomSearch.CriteriaName || selectedCustomSearch.Text || selectedCustomSearch.Name || ""),
                    strDateFrom: formatDateToDDMMMYYYY(filterValues.createdDateFrom),
                    strDateTo: formatDateToDDMMMYYYY(filterValues.createdDateTo),
                    strEstimatedClosureFrom: formatDateToDDMMMYYYY(filterValues.closureDateFrom),
                    strEstimatedClosureTo: formatDateToDDMMMYYYY(filterValues.closureDateTo),
                    strLostReason: null,
                    strStageList: finalStageList
                }
            };

            console.log("Fetching deals with payload:", payload);

            const response = await fetch(USER_CRM_STAGEWISE, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();
            const details = result?.Details || [];

            // Map details to an object keyed by StageID
            const mappedDeals = {};
            details.forEach(item => {
                const sId = (item.StageID || item.StageId || item.Id)?.toString();
                if (sId) {
                    mappedDeals[sId] = item.DealDetailsList || [];
                }
            });

            setDealsByStage(mappedDeals);
        } catch (error) {
            console.error("Error fetching deal data:", error);
            setDealsByStage({});
        } finally {
            setLoading(false);
        }
    };

    const hasInitializedRef = useRef(false);
    const prevDepsRef = useRef({ selectedPipeline, selectedCustomSearch });

    // --- Load stages + deals when selected pipeline changes ---
    useEffect(() => {
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            fetchPipelines();
            fetchCustomSearch();
        }
    }, []);

    // Handle navigation from Dashboard
    useEffect(() => {
        if (location.state?.selectedPipelineName && pipelines.length > 0) {
            const found = pipelines.find(p =>
                p.PipelineName.toLowerCase() === location.state.selectedPipelineName.toLowerCase()
            );
            if (found) {
                setSelectedPipeline(found);
                setDropdownSearch(location.state.selectedPipelineName);
            }
        }
    }, [location.state, pipelines]);

    useEffect(() => {
        const prev = prevDepsRef.current;
        if (prev.selectedPipeline === selectedPipeline && prev.selectedCustomSearch === selectedCustomSearch) {
            return;
        }
        prevDepsRef.current = { selectedPipeline, selectedCustomSearch };

        const pipelineId = selectedPipeline?.PipeLineID;
        if (pipelineId) {
            fetchStages(pipelineId);
        }
    }, [selectedPipeline, selectedCustomSearch]);

    // --- Filtered pipelines for dropdown search ---
    const filteredPipelines = useMemo(() => {
        if (!dropdownSearch) return pipelines;
        return pipelines.filter(p =>
            p.PipelineName.toLowerCase().includes(dropdownSearch.toLowerCase())
        );
    }, [pipelines, dropdownSearch]);

    const filteredCustomSearch = useMemo(() => {
        if (!customSearchDropdownSearch) return customSearchOptions;
        return customSearchOptions.filter(opt =>
            (opt.Text || opt.Name || opt.CriteriaName || '').toLowerCase().includes(customSearchDropdownSearch.toLowerCase())
        );
    }, [customSearchOptions, customSearchDropdownSearch]);

    // --- Compute summary ---
    const totalAmount = useMemo(() => {
        let total = 0;
        Object.values(dealsByStage).forEach(dealsList => {
            dealsList.forEach(deal => {
                total += parseFloat(deal.Amount || deal.amount || 0);
            });
        });
        return total;
    }, [dealsByStage]);

    // --- Compute applied filters count ---
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (selectedSalesOwners.length > 0) count++;
        if (filterPipeline) count++;
        if (selectedFilterStages.length > 0) count++;
        if (filterValues.createdDateFrom !== DEFAULT_DATE_FROM) count++;
        if (filterValues.createdDateTo !== DEFAULT_DATE_TO) count++;
        if (filterValues.estimatedAmountFrom !== '0') count++;
        if (filterValues.estimatedAmountTo !== '9999999') count++;
        if (filterValues.expiryDealsOnly) count++;
        if (filterValues.isDeletedOnly) count++;
        if (filterValues.closureDateFrom) count++;
        if (filterValues.closureDateTo) count++;
        return count;
    }, [selectedSalesOwners, filterPipeline, selectedFilterStages, filterValues]);

    // --- Export to Excel ---
    const handleExport = () => {
        const allDeals = [];
        Object.keys(dealsByStage).forEach(stageId => {
            const stage = stages.find(s => s.Id === stageId);
            const deals = dealsByStage[stageId] || [];
            deals.forEach(deal => {
                allDeals.push({
                    "Lead Name": deal.LeadName || deal.personName || deal.PersonName || deal.Name || deal.name || "",
                    "Lead ID": deal.LeadID || deal.dealId || deal.id || deal.LeadId || deal.Id || deal.DealID || "",
                    "Stage Name": stage?.StageName || deal.StageName || deal.stageName || deal.PipeLineStage || "",
                    "Probability": deal.Probability || deal.probability || "",
                    "Amount": parseFloat(deal.Amount || deal.amount || 0).toFixed(2),
                    "Max Amount": parseFloat(deal.MaxAmount || deal.maxAmount || deal.Amount || 0).toFixed(2),
                    "Total Amount": parseFloat(deal.TotalAmount || deal.totalAmount || deal.Amount || 0).toFixed(2),
                    "Total Deals": deal.TotalDeal || deal.totalDeal || 1,
                    "Remarks": deal.Remarks || deal.remarks || deal.remark || "",
                    "Created Date": deal.CreatedDate || deal.CreatedOn || deal.createdDate || deal.createdOn || deal.addedOn || deal.AddedOn || deal.Date || "",
                    "Created By": (deal.FName && deal.LName) ? `${deal.FName} ${deal.LName}` : (deal.CreatedByName || deal.AddedByName || deal.createdBy || deal.addedBy || deal.AddedBy || deal.CreatedBy || deal.createdby || ""),
                    "Estimated Closure Date": deal.EstimatedClosureDate || deal.ClosureDate || deal.closureDate || deal.estimatedClosureDate || deal.EstimatedDate || deal.TargetDate || "",
                    "Expiry In": deal.ExpiryIn || deal.expiryDate || deal.ExpiryDate || deal.expiryIn || "",
                    "Deal Owner": deal.DealOwnerName || deal.OwnerName || deal.dealOwner || deal.ownerName || deal.DealOwner || deal.owner || deal.Owner || "",
                    "Owner": deal.Owner || deal.DealOwnerName || deal.OwnerName || deal.ownerName || deal.dealOwner || deal.DealOwner || deal.owner || "",
                    "Lost Reason": deal.LostReason || deal.lostReason || deal.Reason || deal.reason || "",
                    "Lost Remark": deal.LostRemark || deal.lostRemark || deal.LostRemarks || deal.lostRemarks || ""
                });
            });
        });

        if (allDeals.length === 0) {
            alert("No deals to export based on current filters.");
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(allDeals);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Pipeline Deals");
        XLSX.writeFile(workbook, `PipelineDeals_${new Date().toLocaleDateString().replace(/\//g, '-')}.xlsx`);
    };

    // --- Get deals for a specific stage ---
    const getDealsForStage = (stageId) => {
        const sId = stageId?.toString();
        let stageDeals = dealsByStage[sId] || [];
        if (searchTerm) {
            stageDeals = stageDeals.filter(d =>
                (d.LeadName || d.personName || '').toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return stageDeals;
    };

    // --- Get deal count and amount for a stage ---
    const getStageSummary = (stageId) => {
        const stageDeals = getDealsForStage(stageId);
        return {
            count: stageDeals.length,
            amount: stageDeals.reduce((sum, d) => sum + parseFloat(d.Amount || d.amount || 0), 0),
        };
    };

    // --- Handle pipeline selection ---
    const handleSelectPipeline = (pipeline) => {
        setSelectedPipeline(pipeline);
        setDropdownOpen(false);
        setDropdownSearch('');
    };

    // --- Today for date formatting in summary ---
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const formatDateMMM = (dateStr) => {
        if (!dateStr || dateStr === 'N/A' || dateStr === '0' || dateStr === 'null') return '';

        let dateObj = new Date(dateStr);

        // If native parsing fails, try to handle common formats
        if (isNaN(dateObj.getTime())) {
            const parts = dateStr.split(/[\s\-\/\,]+/);
            if (parts.length >= 3) {
                // Try to guess format
                if (parts[0].length === 4) { // YYYY-MM-DD
                    dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                } else if (parts[2].length === 4) { // DD-MM-YYYY or MM-DD-YYYY
                    // We'll assume DD-MM-YYYY as it's common in this app's display
                    dateObj = new Date(parts[2], parts[1] - 1, parts[0]);
                }
            }
        }

        if (isNaN(dateObj.getTime())) return dateStr;

        const d = String(dateObj.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const m = months[dateObj.getMonth()];
        const y = dateObj.getFullYear();

        return `${d}-${m}-${y}`;
    };

    // --- Handle Edit Deal ---
    const handleEditDeal = (deal, stage, mode = 'edit') => {
        setSelectedDealForEdit({
            ...deal,
            pipelineId: selectedPipeline?.PipeLineID,
            stageId: stage?.StageID,
            pipelineName: selectedPipeline?.PipelineName,
            stageName: stage?.StageName
        });
        setEditMode(mode);
        setView('edit');
        setActiveMenu(null);
    };

    const handleBackToPipeline = () => {
        setView('kanban');
        setSelectedDealForEdit(null);
    };

    const handleUpdateDeal = (updatedData) => {
        if (selectedPipeline?.PipeLineID) {
            fetchStages(selectedPipeline.PipeLineID);
        }
        handleBackToPipeline();
    };

    return (
        <div className="pipeline-deal-module">
            {/* --- Top Bar (Modernized) --- */}
            <SubHeader
                pageName="Deals"
                pageCode="PAG10038"
                title={(
                    <div className="pipeline-header-left-group">
                        <h1 className="pipeline-main-title">Deals</h1>
                        <div className="pipeline-selector-wrapper">
                            <div className="pipeline-dropdown-wrapper">
                                <button
                                    className="pipeline-dropdown-trigger"
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                    style={{
                                        borderRadius: '12px',
                                        background: '#f8fafc',
                                        border: '1.5px solid #e2e8f0',
                                        padding: '6px 14px',
                                        height: '40px',
                                        minWidth: '180px',
                                        fontWeight: '600',
                                        color: '#1e293b'
                                    }}
                                >
                                    {selectedPipeline?.PipelineName || 'Select Pipeline'}
                                    <ChevronDown size={14} style={{ marginLeft: '8px', color: '#64748b' }} />
                                </button>

                                {dropdownOpen && (
                                    <>
                                        <div className="pipeline-dropdown-backdrop" onClick={() => { setDropdownOpen(false); setDropdownSearch(''); }} />
                                        <div className="pipeline-dropdown-menu" style={{ zIndex: 2000, borderRadius: '12px' }}>
                                            <input
                                                className="pipeline-dropdown-search"
                                                type="text"
                                                placeholder="Search pipeline..."
                                                value={dropdownSearch}
                                                onChange={(e) => setDropdownSearch(e.target.value)}
                                                autoFocus
                                            />
                                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                {filteredPipelines.map(p => (
                                                    <div
                                                        key={p.PipeLineID}
                                                        className={`pipeline-dropdown-item ${selectedPipeline?.PipeLineID === p.PipeLineID ? 'selected' : ''}`}
                                                        onClick={() => handleSelectPipeline(p)}
                                                    >
                                                        {p.PipelineName}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                centerContent={(
                    <div className="pipeline-summary-info">
                        <span className="summary-label">Total Amount :</span>
                        <span className="summary-amount">{totalAmount.toLocaleString()} Rs.</span>
                        <span className="summary-date-range">From {formatDateToDDMMMYYYY(filterValues.createdDateFrom)} To {formatDateToDDMMMYYYY(filterValues.createdDateTo)}</span>
                    </div>
                )}
                showAdd={!!dealPerms?.Add}
                onAddClick={dealPerms?.Add ? () => setIsLeadActionOpen(true) : null}
                showFilter={!!dealPerms?.Search}
                onFilterClick={dealPerms?.Search ? () => {
                    setShowFilterPanel(!showFilterPanel);
                    if (!showFilterPanel) fetchSalesOwners();
                } : null}
                showExport={!!dealPerms?.Export}
                onExportClick={dealPerms?.Export ? handleExport : null}
                showSettings={true}
                showAdvanceFilter={false}
                showViewSwitcher={false}
                viewMode={view === 'kanban' ? 'grid' : 'table'}
                setViewMode={(m) => setView(m === 'grid' ? 'kanban' : 'table')}
                filterCount={activeFilterCount}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                showSearch={!!dealPerms?.Search}
                customPadding="0 24px"
            >
                {/* Right side actions - matching Image 5 layout */}
                {dealPerms?.Search !== false && (
                <div className="pipeline-header-right-actions">
                    <div className="pipeline-custom-search-and-actions">
                        <div className="pipeline-search-wrapper" style={{ border: 'none', padding: 0, background: 'transparent', width: 'auto' }}>
                            <div className="pipeline-dropdown-wrapper" style={{ width: '220px' }}>
                                <button
                                    className="pipeline-custom-search-trigger"
                                    onClick={() => setCustomSearchDropdownOpen(!customSearchDropdownOpen)}
                                >
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {selectedCustomSearch?.CriteriaName || selectedCustomSearch?.Text || selectedCustomSearch?.Name || 'Select Custom Search'}
                                    </span>
                                    <span className="dropdown-arrow" style={{ fontSize: (selectedCustomSearch?.CriteriaName || selectedCustomSearch?.Text || selectedCustomSearch?.Name) ? '0px' : '10px', marginLeft: '8px', color: '#64748b' }}>
                                        {(selectedCustomSearch?.CriteriaName || selectedCustomSearch?.Text || selectedCustomSearch?.Name) ? '' : '▼'}
                                    </span>
                                </button>

                                {customSearchDropdownOpen && (
                                    <>
                                        <div className="pipeline-dropdown-backdrop" onClick={() => { setCustomSearchDropdownOpen(false); setCustomSearchDropdownSearch(''); }} />
                                        <div className="pipeline-custom-search-menu">
                                            <input
                                                className="pipeline-dropdown-search"
                                                type="text"
                                                placeholder="Search..."
                                                value={customSearchDropdownSearch}
                                                onChange={(e) => setCustomSearchDropdownSearch(e.target.value)}
                                                autoFocus
                                                style={{
                                                    padding: '10px 12px',
                                                    border: 'none',
                                                    borderBottom: '1px solid #f1f5f9',
                                                    width: '100%',
                                                    fontSize: '13.5px',
                                                    outline: 'none'
                                                }}
                                            />
                                            <div style={{ maxHeight: '250px', overflowY: 'auto', padding: '4px 0' }}>
                                                <div
                                                    className={`pipeline-dropdown-item ${!selectedCustomSearch ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setSelectedCustomSearch(null);
                                                        setCustomSearchDropdownOpen(false);
                                                        setCustomSearchDropdownSearch('');
                                                    }}
                                                >
                                                    All Leads
                                                </div>
                                                {filteredCustomSearch.length > 0 ? (
                                                    filteredCustomSearch.map((opt, idx) => {
                                                        const isSelected = (opt.ID && selectedCustomSearch?.ID === opt.ID) || (opt.Id && selectedCustomSearch?.Id === opt.Id);
                                                        return (
                                                            <div
                                                                key={idx}
                                                                className={`pipeline-dropdown-item ${isSelected ? 'selected' : ''}`}
                                                                onClick={() => {
                                                                    setSelectedCustomSearch(opt);
                                                                    setCustomSearchDropdownOpen(false);
                                                                    setCustomSearchDropdownSearch('');
                                                                }}
                                                            >
                                                                {opt.CriteriaName || opt.Text || opt.Name}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div style={{ padding: '12px', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>No options found</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                )}

                {/* Advanced Filter Panel Overlay (Preserved Logic) */}
                {showFilterPanel && (
                    <div className="advanced-filter-panel shadow-lg">
                        <div className="filter-panel-header">
                            <h3>Filters</h3>
                            <button className="filter-close-btn" onClick={() => setShowFilterPanel(false)}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="filter-panel-scrollable">
                            {/* 1. Sales Owner */}
                            <div className="filter-section">
                                <label className="filter-label">Sales owner</label>
                                <div className="custom-owner-dropdown">
                                    <div
                                        className="owner-dropdown-trigger"
                                        onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
                                    >
                                        <div className="selected-owner-tags">
                                            {selectedSalesOwners.length > 0 ? (
                                                selectedSalesOwners.map(owner => (
                                                    <div key={owner.User_ID} className="owner-tag" onClick={(e) => e.stopPropagation()}>
                                                        <span>{owner.DisplayName}</span>
                                                        <span
                                                            className="owner-tag-remove"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedSalesOwners(prev => prev.filter(o => o.User_ID !== owner.User_ID));
                                                            }}
                                                        >
                                                            <X size={12} />
                                                        </span>
                                                    </div>
                                                ))
                                            ) : (
                                                <span className="placeholder-text">Select Sales Owner</span>
                                            )}
                                        </div>
                                        <ChevronDown size={18} color="#64748b" />
                                    </div>

                                    {ownerDropdownOpen && (
                                        <div className="owner-dropdown-menu">
                                            <div className="owner-search">
                                                <Search size={14} />
                                                <input
                                                    type="text"
                                                    placeholder="Search Sales Owner..."
                                                    value={ownerSearchTerm}
                                                    onChange={(e) => setOwnerSearchTerm(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            <div className="owner-list">
                                                {salesOwners.filter(o =>
                                                    (o.DisplayName && o.DisplayName.toLowerCase().includes(ownerSearchTerm.toLowerCase())) ||
                                                    (o.User_ID || '').toString().includes(ownerSearchTerm)
                                                ).map((owner) => (
                                                    <div
                                                        key={owner.User_ID}
                                                        className={`owner-item ${selectedSalesOwners.some(o => o.User_ID === owner.User_ID) ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            if (selectedSalesOwners.some(o => o.User_ID === owner.User_ID)) {
                                                                setSelectedSalesOwners(prev => prev.filter(o => o.User_ID !== owner.User_ID));
                                                            } else {
                                                                setSelectedSalesOwners(prev => [...prev, owner]);
                                                            }
                                                        }}
                                                    >
                                                        <input type="checkbox" checked={selectedSalesOwners.some(o => o.User_ID === owner.User_ID)} readOnly style={{ marginRight: "8px", cursor: "pointer" }} />{owner.DisplayName}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2. Pipeline Filter */}
                            <div className="filter-section">
                                <label className="filter-label">Pipeline</label>
                                <div className="custom-owner-dropdown">
                                    <div
                                        className="owner-dropdown-trigger"
                                        onClick={() => setPipelineDropdownOpen(!pipelineDropdownOpen)}
                                    >
                                        <span>{filterPipeline ? filterPipeline.PipelineName : "Select Pipeline"}</span>
                                        <ChevronDown size={18} color="#64748b" />
                                    </div>
                                    {pipelineDropdownOpen && (
                                        <div className="owner-dropdown-menu">
                                            <div className="owner-list">
                                                {pipelines.map((p) => (
                                                    <div
                                                        key={p.PipeLineID}
                                                        className={`owner-item ${filterPipeline?.PipeLineID === p.PipeLineID ? "selected" : ""}`}
                                                        onClick={() => {
                                                            setFilterPipeline(p);
                                                            fetchFilterStages(p.PipeLineID);
                                                            setPipelineDropdownOpen(false);
                                                        }}
                                                    >
                                                        {p.PipelineName}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="filter-section">
                                <label className="filter-label">Stages</label>
                                <div className="custom-owner-dropdown">
                                    <div
                                        className="owner-dropdown-trigger"
                                        onClick={() => setStageDropdownOpen(!stageDropdownOpen)}
                                    >
                                        <span className="selected-owner-tags">
                                            {selectedFilterStages.length > 0
                                                ? `${selectedFilterStages.length} Stages Selected`
                                                : "Select Stage"}
                                        </span>
                                        <ChevronDown size={18} color="#64748b" />
                                    </div>
                                    {stageDropdownOpen && (
                                        <div className="owner-dropdown-menu">
                                            <div className="owner-list">
                                                <div
                                                    className="owner-item"
                                                    onClick={() => setSelectedFilterStages([])}
                                                >
                                                    Clear Selection
                                                </div>
                                                {filterStages.map((s) => {
                                                    const isSelected = selectedFilterStages.some(item => item.Id === s.Id);
                                                    return (
                                                        <div
                                                            key={s.Id}
                                                            className={`owner-item stage-multi-item ${isSelected ? "selected" : ""}`}
                                                            onClick={() => {
                                                                if (isSelected) {
                                                                    setSelectedFilterStages(selectedFilterStages.filter(item => item.Id !== s.Id));
                                                                } else {
                                                                    setSelectedFilterStages([...selectedFilterStages, s]);
                                                                }
                                                            }}
                                                        >
                                                            <div className={`radio-circle ${isSelected ? 'checked' : ''}`}></div>
                                                            <span>{s.Name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 4. Created Date Range */}
                            <div className="filter-row">
                                <div className="filter-col">
                                    <label className="filter-label">Created From</label>
                                    <div className="premium-input-wrapper" style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="filter-date-input"
                                            readOnly
                                            value={formatDateToDDMMMYYYY(filterValues.createdDateFrom)}
                                            onClick={() => createdFromRef.current.showPicker && createdFromRef.current.showPicker()}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <Calendar
                                            size={14}
                                            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', cursor: 'pointer', pointerEvents: 'none' }}
                                        />
                                        <input
                                            type="date"
                                            ref={createdFromRef}
                                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0, width: '100%', height: '100%' }}
                                            value={filterValues.createdDateFrom}
                                            onChange={(e) => setFilterValues({ ...filterValues, createdDateFrom: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="filter-col">
                                    <label className="filter-label">Created To</label>
                                    <div className="premium-input-wrapper" style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="filter-date-input"
                                            readOnly
                                            value={formatDateToDDMMMYYYY(filterValues.createdDateTo)}
                                            onClick={() => createdToRef.current.showPicker && createdToRef.current.showPicker()}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <Calendar
                                            size={14}
                                            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', cursor: 'pointer', pointerEvents: 'none' }}
                                        />
                                        <input
                                            type="date"
                                            ref={createdToRef}
                                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0, width: '100%', height: '100%' }}
                                            value={filterValues.createdDateTo}
                                            onChange={(e) => setFilterValues({ ...filterValues, createdDateTo: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 5. Amount Range */}
                            <div className="filter-row">
                                <div className="filter-col">
                                    <label className="filter-label">Amount From</label>
                                    <div className="filter-input-wrapper amount">
                                        <input
                                            type="number"
                                            className="filter-text-input"
                                            placeholder="Min"
                                            value={filterValues.estimatedAmountFrom}
                                            onChange={(e) => setFilterValues({ ...filterValues, estimatedAmountFrom: e.target.value })}
                                        />
                                        <span className="currency-symbol">₹</span>
                                    </div>
                                </div>
                                <div className="filter-col">
                                    <label className="filter-label">Amount To</label>
                                    <div className="filter-input-wrapper amount">
                                        <input
                                            type="number"
                                            className="filter-text-input"
                                            placeholder="Max"
                                            value={filterValues.estimatedAmountTo}
                                            onChange={(e) => setFilterValues({ ...filterValues, estimatedAmountTo: e.target.value })}
                                        />
                                        <span className="currency-symbol">₹</span>
                                    </div>
                                </div>
                            </div>

                            {/* 6. Estimated Closure Date Range */}
                            <div className="filter-row">
                                <div className="filter-col">
                                    <label className="filter-label">Estimated Closure Date From</label>
                                    <div className="premium-input-wrapper" style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="filter-date-input"
                                            readOnly
                                            value={formatDateToDDMMMYYYY(filterValues.closureDateFrom)}
                                            onClick={() => closureFromRef.current.showPicker && closureFromRef.current.showPicker()}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <Calendar
                                            size={14}
                                            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', cursor: 'pointer', pointerEvents: 'none' }}
                                        />
                                        <input
                                            type="date"
                                            ref={closureFromRef}
                                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0, width: '100%', height: '100%' }}
                                            value={filterValues.closureDateFrom}
                                            onChange={(e) => setFilterValues({ ...filterValues, closureDateFrom: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="filter-col">
                                    <label className="filter-label">Estimated Closure Date To</label>
                                    <div className="premium-input-wrapper" style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="filter-date-input"
                                            readOnly
                                            value={formatDateToDDMMMYYYY(filterValues.closureDateTo)}
                                            onClick={() => closureToRef.current.showPicker && closureToRef.current.showPicker()}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <Calendar
                                            size={14}
                                            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', cursor: 'pointer', pointerEvents: 'none' }}
                                        />
                                        <input
                                            type="date"
                                            ref={closureToRef}
                                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0, width: '100%', height: '100%' }}
                                            value={filterValues.closureDateTo}
                                            onChange={(e) => setFilterValues({ ...filterValues, closureDateTo: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 6. Checkboxes */}
                            <div className="filter-row" style={{ marginTop: '8px' }}>
                                <label className="filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterValues.expiryDealsOnly}
                                        onChange={(e) => setFilterValues({ ...filterValues, expiryDealsOnly: e.target.checked })}
                                    />
                                    <span>Expiry Deals Only</span>
                                </label>
                                <label className="filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={filterValues.isDeletedOnly}
                                        onChange={(e) => setFilterValues({ ...filterValues, isDeletedOnly: e.target.checked })}
                                    />
                                    <span>Is Deleted Only</span>
                                </label>
                            </div>
                        </div>

                        <div className="filter-actions">
                            <button
                                className="filter-reset-btn"
                                onClick={() => {
                                    setSelectedSalesOwners([]);
                                    setFilterPipeline(null);
                                    setFilterStages([]);
                                    setSelectedFilterStages([]);
                                    const defaultFilters = {
                                        createdDateFrom: '2026-02-19',
                                        createdDateTo: '2026-02-26',
                                        estimatedAmountFrom: '0',
                                        estimatedAmountTo: '9999999',
                                        expiryDealsOnly: false,
                                        isDeletedOnly: false,
                                        closureDateFrom: '',
                                        closureDateTo: ''
                                    };
                                    setFilterValues(defaultFilters);
                                    // Trigger fetch with defaults
                                    fetchDealData(selectedPipeline?.PipeLineID, []);
                                }}
                            >
                                Reset Deal Filters
                            </button>
                            <button className="filter-apply-btn" onClick={() => {
                                setShowFilterPanel(false);

                                // If pipeline in filter is different, update the main selected pipeline
                                // which will trigger fetchStages -> fetchDealData
                                if (filterPipeline && filterPipeline.PipeLineID !== selectedPipeline?.PipeLineID) {
                                    setSelectedPipeline(filterPipeline);
                                } else {
                                    const stagesToUse = selectedFilterStages.length > 0 ? selectedFilterStages.map(s => s.Id || s.Code || s.StageID) : [];
                                    fetchDealData(selectedPipeline?.PipeLineID, stagesToUse);
                                }
                            }}>
                                Apply Filter
                            </button>
                        </div>
                    </div>
                )}
            </SubHeader>

            {/* --- Kanban Board --- */}
            <div className={`pipeline-kanban-container ${view === 'edit' ? 'blur-background' : ''}`}>
                {
                    loading ? (
                        <div className="pipeline-loading-localized" style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            background: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 50
                        }} >
                            <Loader />
                        </div >
                    ) : (
                        stages.length > 0 ? (
                            stages.map(stage => {
                                const summary = getStageSummary(stage.StageID);
                                const stageDeals = getDealsForStage(stage.StageID);

                                return (
                                    <div key={stage.StageID} className="pipeline-stage-column">
                                        {/* Column Header */}
                                        <div className="pipeline-stage-header">
                                            <div className="stage-header-content">
                                                <h3 className="pipeline-stage-name">{stage.StageName}</h3>
                                                <div className="pipeline-stage-meta">
                                                    {summary.amount > 0 && (
                                                        <span className="stage-amount">
                                                            <TrendingUp size={12} />
                                                            {summary.amount.toLocaleString()}
                                                        </span>
                                                    )}
                                                    <span className="stage-deals-count">
                                                        <Info size={12} />
                                                        {summary.count} deals
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Column Body */}
                                        <div className="pipeline-stage-body">
                                            {stageDeals.length > 0 ? (
                                                stageDeals.map(deal => {
                                                    const isExpired = deal.ExpiryIn === "expired";
                                                    return (
                                                        <div
                                                            key={deal.DealID || deal.id}
                                                            className={`pipeline-deal-card ${!isExpired ? 'not-expired' : 'expired'} ${activeMenu === (deal.DealID || deal.id) ? 'menu-active' : ''}`}
                                                        >
                                                            <div className="deal-card-top">
                                                                <div className="deal-card-name">
                                                                    <div className="deal-card-amount-wrapper"
                                                                        onMouseEnter={() => setHoveredDealId(deal.DealID || deal.id)}
                                                                        onMouseLeave={() => setHoveredDealId(null)}
                                                                    >
                                                                        <span className="deal-card-amount">
                                                                            {parseFloat(deal.Amount || deal.amount || 0).toFixed(2)}
                                                                        </span>
                                                                        {hoveredDealId === (deal.DealID || deal.id) && (
                                                                            <div className="amount-tooltip">
                                                                                {parseFloat(deal.Amount || deal.amount || 0).toFixed(2)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <span className="deal-card-dot">•</span>
                                                                    <span className="deal-person">{deal.LeadName || deal.personName}</span>
                                                                </div>
                                                                <div className="deal-card-actions">
                                                                    <button
                                                                        className="deal-card-edit-btn"
                                                                        onClick={() => handleEditDeal(deal, stage)}
                                                                    >
                                                                        <Pencil size={14} />
                                                                    </button>
                                                                    <div className="deal-card-menu-wrapper">
                                                                        <button
                                                                            className="deal-card-menu-btn"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                const dealId = deal.DealID || deal.id;
                                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                                const windowWidth = window.innerWidth;
                                                                                const windowHeight = window.innerHeight;

                                                                                let top = rect.bottom + 2;
                                                                                let left = rect.left;
                                                                                let bottom = 'auto';

                                                                                // Check if it fits on the right
                                                                                if (left + 210 > windowWidth) {
                                                                                    left = rect.right - 210;
                                                                                }
                                                                                if (left < 10) left = 10;

                                                                                // Intelligent vertical positioning
                                                                                const menuHeight = 350; 
                                                                                if (top + menuHeight > windowHeight) {
                                                                                    // Open UPWARDS: Use bottom positioning for perfect attachment
                                                                                    top = 'auto';
                                                                                    bottom = (windowHeight - rect.top + 2) + 'px';
                                                                                } else {
                                                                                    top = top + 'px';
                                                                                }

                                                                                setMenuCoords({ top, left, bottom });
                                                                                setSelectedDealForMenu(deal);
                                                                                setSelectedStageForMenu(stage);
                                                                                setActiveMenu(activeMenu === dealId ? null : dealId);
                                                                            }}
                                                                        >
                                                                            ⋮
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="deal-card-expiry">
                                                                {isExpired
                                                                    ? <span style={{ color: '#dc2626' }}>Expiry in : expired {formatDateMMM(deal.ExpiryDate || deal.expiryDate || '')}</span>
                                                                    : <span style={{ color: '#64748b' }}>Expiry in : {deal.ExpiryIn || deal.expiryDate || ''}</span>
                                                                }
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                null /* Empty columns just stay empty visually */
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="pipeline-no-stages">
                                <p>No stages found for this pipeline.</p>
                            </div>
                        )
                    )}
            </div >

            {/* --- Global Deal Menu (Outside columns to avoid clipping) --- */}
            {activeMenu && (
                <>
                    <div className="menu-backdrop" onClick={() => setActiveMenu(null)} />
                    <div
                        className="deal-card-dropdown"
                        style={{
                            position: 'fixed',
                            top: menuCoords.top,
                            bottom: menuCoords.bottom,
                            left: `${menuCoords.left}px`,
                            zIndex: 2000001 // Higher than everything else
                        }}
                    >
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'add-followup'); setActiveMenu(null); }}><UserCheck size={16} className="item-icon followup" /> Add Followup</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'email'); setActiveMenu(null); }}><Mail size={16} className="item-icon mail" /> Send Mail</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'sms'); setActiveMenu(null); }}><MessageSquare size={16} className="item-icon sms" /> Send SMS</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'voice'); setActiveMenu(null); }}><Mic size={16} className="item-icon voice" /> Send Voice</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'add-notes'); setActiveMenu(null); }}><FileText size={16} className="item-icon note" /> Add Note</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'upload-doc'); setActiveMenu(null); }}><Download size={16} className="item-icon upload" /> Upload Document</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'task'); setActiveMenu(null); }}><CheckSquare size={16} className="item-icon task" /> Add Task</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'appointment'); setActiveMenu(null); }}><Calendar size={16} className="item-icon appointment" /> Add Appointment</div>
                        <div className="dropdown-item" onClick={() => { handleEditDeal(selectedDealForMenu, selectedStageForMenu, 'whatsapp'); setActiveMenu(null); }}><MessageSquare size={16} className="item-icon whatsapp" style={{ color: '#25D366' }} /> WhatsApp</div>
                    </div>
                </>
            )}

            {/* --- Edit Overlay --- */}
            {
                view === 'edit' && (
                    <div className="pipeline-edit-overlay">
                        <div className="pipeline-edit-modal">
                            {editMode === 'edit' ? (
                                <PipelineDealActionsOperation
                                    deal={selectedDealForEdit}
                                    mode="edit"
                                    onBack={handleBackToPipeline}
                                    onUpdate={handleUpdateDeal}
                                />
                            ) : (
                                <div className="premium-operation-container">
                                    <div className="premium-operation-header">
                                        <button className="premium-back-btn" onClick={handleBackToPipeline}>
                                            <ChevronLeft size={20} />
                                        </button>
                                        <div className="premium-header-text">
                                            <h2 className="premium-title">
                                                {editMode === 'email' ? 'Send Mail' :
                                                    editMode === 'add-followup' ? 'Add Followup' :
                                                        editMode === 'add-notes' ? 'Add Notes' :
                                                            editMode === 'task' ? 'Add Task' :
                                                                editMode === 'appointment' ? 'Add Appointment' :
                                                                    editMode === 'upload-doc' ? 'Upload Document' :
                                                                        editMode === 'sms' ? 'Send SMS' :
                                                                            editMode === 'voice' ? 'Send Voice' :
                                                                                editMode === 'whatsapp' ? 'WhatsApp Messaging' :
                                                                                    'Action Operation'}
                                            </h2>
                                            <span className="premium-subtitle">{selectedDealForEdit?.personName || selectedDealForEdit?.LeadName || 'Deal Details'}</span>
                                        </div>
                                    </div>
                                    <div className="premium-operation-content" style={{ padding: 0, overflowY: 'auto' }}>
                                        <LeadActionOperation
                                            entity={{
                                                enquiryId: selectedDealForEdit?.LeadID || selectedDealForEdit?.leadId || selectedDealForEdit?.id,
                                                personName: selectedDealForEdit?.LeadName || selectedDealForEdit?.personName,
                                                amount: selectedDealForEdit?.Amount || selectedDealForEdit?.amount,
                                                pipelineName: selectedDealForEdit?.pipelineName,
                                                stageName: selectedDealForEdit?.stageName
                                            }}
                                            mode={editMode === 'email' ? 'mail' :
                                                editMode === 'add-followup' ? 'followup' :
                                                    editMode === 'add-notes' ? 'notes' :
                                                        editMode === 'task' ? 'addtask' :
                                                            editMode === 'appointment' ? 'addappointment' :
                                                                editMode === 'upload-doc' ? 'upload-document' :
                                                                    editMode}
                                            enableTemplatePreview={true}
                                            onBack={handleBackToPipeline}
                                            onSuccess={() => {
                                                handleUpdateDeal();
                                                fetchDealData(selectedPipeline?.PipeLineID);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Add Deal Drawer */}
            <Drawer
                anchor="right"
                open={isLeadActionOpen}
                onClose={() => setIsLeadActionOpen(false)}
                sx={{ zIndex: 1000000 }}
                PaperProps={{
                    className: 'pipeline-add-deal-drawer',
                    sx: {
                        width: { xs: '100vw', sm: '80vw', md: '70vw', lg: '800px' },
                        maxWidth: '100%',
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 0,
                        borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
                        boxShadow: '-4px 0 15px rgba(0, 0, 0, 0.1)'
                    }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <PipelineDealActionsOperation
                        mode="add"
                        onBack={() => setIsLeadActionOpen(false)}
                        onUpdate={() => {
                            setIsLeadActionOpen(false);
                            fetchDealData(selectedPipeline?.PipeLineID);
                        }}
                    />
                </Box>
            </Drawer>
        </div >
    );
};

export default PipelineDealModule;
